import { EditOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Empty, Flex, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as commonColumns from "../../../common/columns";
import { canEditStandbyPeriod } from "../../../common/utils/canEditStandbyPeriod";
import { formatTotalTime } from "../../../common/utils/formatTotalTime";
import { buildUserDisplayNameById } from "../../../common/utils/userDisplayName";
import { ticketActions } from "../../../features/ticket/ticketSlice";
import type { TicketStandbyPeriod, UserLimitedList } from "../../generated-axios-client";
import type { RootState } from "../../store";
import type { StandbyPeriodEditPayload } from "./EditStandbyPeriodModal";
import { EditStandbyPeriodModal } from "./EditStandbyPeriodModal";
import styles from "./StandbyPeriodsInfo.module.css";

type Props = {
  intl: IntlShape;
  ticketId: string;
  totalStandbySeconds: number | null | undefined;
};

export const StandbyPeriodsInfo = ({ intl, ticketId, totalStandbySeconds }: Props) => {
  const dispatch = useDispatch();
  const periods = useSelector(
    (state: RootState) => state.ticket.standbyPeriods ?? []
  ) as TicketStandbyPeriod[];
  const userList = useSelector(
    (state: RootState) => (state.userstate.list.results ?? []) as UserLimitedList[]
  );
  const currentUser = useSelector((state: RootState) => state.userstate.user);
  const editStatus = useSelector(
    (state: RootState) => state.ticket.editStandbyPeriodStatus
  );

  const [editingPeriod, setEditingPeriod] = useState<TicketStandbyPeriod | null>(null);
  const canEditStandby = canEditStandbyPeriod(currentUser?.groups);

  useEffect(() => {
    if (editStatus === "succeeded") {
      setEditingPeriod(null);
    }
  }, [editStatus]);

  const openStandbyEditor = (period: TicketStandbyPeriod) => {
    // A previous edit, here or on the WO History row, leaves the status at "succeeded",
    // which would close this one the moment it opened.
    dispatch(ticketActions.setEditStandbyPeriodStatus("idle"));
    setEditingPeriod(period);
  };

  const submitStandbyEdit = (payload: StandbyPeriodEditPayload) => {
    if (editingPeriod?.id === undefined) return;
    dispatch(
      ticketActions.editStandbyPeriod({
        periodId: editingPeriod.id,
        ticketId,
        ...payload,
      })
    );
  };

  // Names come from the full user list rather than the WO History name map, which only
  // knows the work order's own people and falls back to "#id" for anyone else.
  const userDisplayNameById = buildUserDisplayNameById(userList);
  const userName = (userId: number | null | undefined) =>
    userId != null ? (userDisplayNameById[userId] ?? `User ${userId}`) : "—";

  if (periods.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({ id: "standby_tab_empty" })}
      />
    );
  }

  const noValue = <Typography.Text type="secondary">—</Typography.Text>;

  const columns: ColumnType<TicketStandbyPeriod>[] = [
    {
      key: "started_at",
      title: intl.formatMessage({ id: "standby_tab_column_start" }),
      render: (_: unknown, period: TicketStandbyPeriod) =>
        period.started_at ? commonColumns.dateTimeValue(period.started_at, intl) : noValue,
    },
    {
      key: "ended_at",
      title: intl.formatMessage({ id: "standby_tab_column_end" }),
      render: (_: unknown, period: TicketStandbyPeriod) =>
        period.ended_at ? commonColumns.dateTimeValue(period.ended_at, intl) : noValue,
    },
    {
      key: "duration",
      title: intl.formatMessage({ id: "standby_tab_column_duration" }),
      // A running period has no duration yet and is left out of Total Standby Time.
      render: (_: unknown, period: TicketStandbyPeriod) =>
        period.ended_at == null ? (
          <Tag color="warning">{intl.formatMessage({ id: "standby_tab_ongoing" })}</Tag>
        ) : (
          formatTotalTime(period.duration_seconds)
        ),
    },
    {
      key: "message",
      title: intl.formatMessage({ id: "standby_tab_column_reason" }),
      render: (_: unknown, period: TicketStandbyPeriod) =>
        period.message?.trim() ? (
          <Typography.Text className={styles.reasonText}>{period.message}</Typography.Text>
        ) : (
          noValue
        ),
    },
    {
      key: "started_by",
      title: intl.formatMessage({ id: "standby_tab_column_started_by" }),
      render: (_: unknown, period: TicketStandbyPeriod) => userName(period.started_by),
    },
    {
      key: "ended_by",
      title: intl.formatMessage({ id: "standby_tab_column_ended_by" }),
      render: (_: unknown, period: TicketStandbyPeriod) =>
        period.ended_at == null ? noValue : userName(period.ended_by),
    },
    ...(canEditStandby
      ? [
          {
            key: "actions",
            render: (_: unknown, period: TicketStandbyPeriod) => (
              <Tooltip title={intl.formatMessage({ id: "standby_period_edit_action" })}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  aria-label={intl.formatMessage({ id: "standby_period_edit_action" })}
                  onClick={() => openStandbyEditor(period)}
                />
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Flex vertical gap="small">
        <Typography.Text type="secondary">
          {intl.formatMessage(
            { id: "standby_tab_total_completed" },
            { total: formatTotalTime(totalStandbySeconds) }
          )}
        </Typography.Text>
        <Table
          columns={columns}
          dataSource={periods}
          rowKey={(period) => period.id ?? period.started_at ?? ""}
          pagination={false}
          size="small"
          scroll={{ x: "max-content" }}
        />
      </Flex>
      {editingPeriod !== null && (
        <EditStandbyPeriodModal
          open
          period={editingPeriod}
          loading={editStatus === "loading"}
          intl={intl}
          onSubmit={submitStandbyEdit}
          onCancel={() => setEditingPeriod(null)}
        />
      )}
    </>
  );
};
